/**
 * ASO-RATING-2026-001 — ask for an App Store rating, at an honest moment, rarely.
 *
 * The system review dialog (`AppStore.requestReview` / `SKStoreReviewController`) is requested only
 * when ALL of these hold, and every one of them is decided here, in a module with no React, no
 * Firebase and no native import, so each rule is assertable with fixtures:
 *
 *   1. A COMPLETED CORE ACTION just happened — a sale durably saved (and, where the store
 *      fiscalizes, signed; where it prints, printed), or a new stock revision saved. The call sites
 *      pass one of `APP_REVIEW_TRIGGERS`; anything else is refused, so a new caller has to be added
 *      here on purpose rather than by pasting a call next to some other toast.
 *   2. NOT THE VERY FIRST SESSION on this device. Someone who has used Atlas once has not formed an
 *      opinion worth asking for.
 *   3. AT MOST ONE REQUEST PER 120 DAYS PER DEVICE, persisted locally. Apple's own cap (three
 *      displays per 365 days per app, per Apple ID) stays the outer bound and Apple alone decides
 *      whether the dialog is shown at all.
 *   4. THE REMOTE KILL SWITCH IS ON. Firebase Remote Config `app_review_prompt_enabled`; unset
 *      means ON (see `isReviewPromptEnabled`).
 *   5. THE PLATFORM CAN DO IT — an iOS binary that contains the native module. Android, web, and
 *      every iOS binary built before this shipped answer `unavailable` and do nothing.
 *
 * DELIBERATELY ABSENT, because App Store Review Guideline 5.6.1 forbids them or because they are
 * dishonest: no custom pre-prompt ("Enjoying Atlas?"), no incentive, no sentiment gate that only
 * asks happy users, and no outcome tracking — Apple never tells an app whether the dialog was shown
 * or what was rated, so any "rated" event would be invented. The single analytics event records the
 * one fact the device actually knows: that the request was made.
 *
 * ANALYTICS AND PROMPTING ARE NEVER LOAD-BEARING: every export resolves, nothing throws outward.
 * A sale screen must not be able to fail because a rating request did.
 */
import AsyncStorage from '@react-native-async-storage/async-storage'

import { resolveClientEnv } from './clientEnvironment'

/** The moments that qualify. Values are what the analytics event carries as `trigger`. */
export const APP_REVIEW_TRIGGERS = Object.freeze({
  SALE_SAVED: 'sale_saved',
  REVISION_COMPLETED: 'revision_completed',
})

const KNOWN_TRIGGERS = new Set(Object.values(APP_REVIEW_TRIGGERS))

/** Captured through the existing PostHog pipeline once the native request has been made. */
export const APP_REVIEW_REQUESTED_EVENT = 'app_review_requested'

/** Firebase Remote Config key. Unset / empty ⇒ ON. See `isReviewPromptEnabled`. */
export const APP_REVIEW_PROMPT_RC_KEY = 'app_review_prompt_enabled'

export const DAY_MS = 24 * 60 * 60 * 1000
export const REVIEW_REQUEST_COOLDOWN_MS = 120 * DAY_MS

/**
 * A session ends after this long with no activity; the next activity after that starts a new one.
 *
 * INACTIVITY, NOT ELAPSED TIME SINCE A START. "Never in the very first session" is a claim about a
 * person's use of the app, and a merchant's first day is one session however it is interrupted: a
 * crash-relaunch, a JS reload (`Updates.reloadAsync()` from the version lockout, the Settings update
 * button, the read-cutover rollback) or a trip to another app inside that span does not end it. A
 * rule measured from the session's START would call a reload 40 minutes into the first day a
 * second session. 30 minutes is the conventional inactivity boundary (PostHog's own session
 * definition), so "session" means here what it means in the analytics.
 *
 * And the other half: a till is launched once and then left open for days, so counting cold starts
 * alone would keep a device that trades all week in its first session forever. Activity is what
 * separates the two — and ACTIVITY MEANS A PERSON TOUCHING THE SCREEN, never "the app is in the
 * foreground". `App.js` calls `useKeepAwake()`, so an iOS till never auto-locks and never
 * backgrounds: a timer that recorded foreground time as activity would hold such a device in its
 * first session for as long as it stays on, which on a till is indefinitely. The bridge records
 * touches (`recordAppSession`, throttled by `SESSION_ACTIVITY_THROTTLE_MS`), so a till left
 * untouched for 30 minutes starts a new session at the next touch, exactly as a phone that locked
 * itself does at the next unlock.
 */
export const SESSION_INACTIVITY_GAP_MS = 30 * 60 * 1000

/**
 * A touch is recorded as activity at most once per this interval. Recording is a read-modify-write
 * on AsyncStorage, and a cashier touches the screen many times a minute. Must stay well below
 * `SESSION_INACTIVITY_GAP_MS`, because the gap is measured from the last RECORDED touch; a test
 * pins that.
 */
export const SESSION_ACTIVITY_THROTTLE_MS = 60 * 1000

/**
 * Whether a touch at `now` should be recorded, given when a touch or activation was last recorded
 * by this process. A mark in the future (the clock went back) is due, so a clock change cannot
 * silence activity recording.
 *
 * @param {number | null} lastRecordedAt
 * @param {number} now
 * @returns {boolean}
 */
export function isActivityRecordDue(lastRecordedAt, now) {
  if (lastRecordedAt == null) return true
  const elapsed = now - lastRecordedAt
  return elapsed < 0 || elapsed >= SESSION_ACTIVITY_THROTTLE_MS
}

/** How long a milestone waits for the boot Remote Config fetch before giving up on this moment. */
export const REMOTE_CONFIG_WAIT_MS = 3000

/**
 * Two keys, not one record, on purpose: the session counter is written by the app-lifecycle bridge
 * and the request latch by the milestone call sites, and those run concurrently. A shared JSON
 * record would be a read-modify-write race in which a session increment can erase a request that
 * was just latched — i.e. the one write that enforces the 120-day rule.
 */
export const SESSION_STORAGE_KEY = 'atlas.appReview.sessions.v1'
export const LAST_REQUEST_STORAGE_KEY = 'atlas.appReview.lastRequestedAt.v1'

/**
 * Whether the remote kill switch allows prompting.
 *
 * DEFAULT ON: an unset or empty key is `true`, so nothing has to be published for the feature to
 * work and a Remote Config fetch that failed on a device that never fetched does not silently
 * disable it.
 *
 * ANYTHING THAT IS NOT `true` IS OFF. Only `false` is documented, but an operator who reaches for
 * this key during an incident and types `False `, `off`, `0` or `"false"` means "stop", and a
 * switch that ignores a typo is a switch that did not fire. The asymmetry is deliberate: a value
 * nobody meant costs at most some reviews, a missed kill costs a customer-visible dialog.
 *
 * @param {unknown} raw — the Remote Config value as returned by `getRemoteConfigValue` (a string)
 * @returns {boolean}
 */
export function isReviewPromptEnabled(raw) {
  if (raw == null) return true
  const value = String(raw).trim().toLowerCase()
  if (value === '') return true
  return value === 'true'
}

/**
 * Parse the persisted session record. Returns null when absent or unreadable.
 *
 * @param {string | null | undefined} raw
 * @returns {{count: number, lastActiveAt: number} | null}
 */
export function parseSessionRecord(raw) {
  if (typeof raw !== 'string' || raw === '') return null
  try {
    const parsed = JSON.parse(raw)
    if (
      parsed &&
      typeof parsed === 'object' &&
      Number.isInteger(parsed.count) &&
      parsed.count >= 1 &&
      Number.isFinite(parsed.lastActiveAt)
    ) {
      return { count: parsed.count, lastActiveAt: parsed.lastActiveAt }
    }
  } catch {
    // fall through
  }
  return null
}

/**
 * Parse the persisted last-request instant.
 *
 * Three answers, not two, because "unreadable" must not collapse into either of the other two:
 *   · `{kind: 'never'}`   — no request has been made from this device (key absent);
 *   · `{kind: 'at', at}`  — the epoch ms of the last request;
 *   · `{kind: 'corrupt'}` — something is stored and it is not an instant.
 *
 * @param {string | null | undefined} raw
 */
export function parseLastRequest(raw) {
  if (raw == null) return { kind: 'never' }
  if (typeof raw === 'string' && /^\d+$/.test(raw)) {
    const at = Number(raw)
    if (Number.isSafeInteger(at) && at > 0) return { kind: 'at', at }
  }
  return { kind: 'corrupt' }
}

/**
 * The half of the decision that needs no I/O and never changes for this binary: is this a known
 * milestone, on a platform and binary that can ask at all.
 *
 * Split out so the caller can answer it BEFORE awaiting Remote Config or touching storage — on
 * Android, web and every pre-module iOS binary nothing else is ever read.
 *
 * @param {{trigger: string, platform: string, nativeAvailable: boolean}} facts
 * @returns {{request: boolean, reason: string}}
 */
export function precheckReviewRequest({ trigger, platform, nativeAvailable }) {
  if (!KNOWN_TRIGGERS.has(trigger)) return { request: false, reason: 'unknown-trigger' }
  if (platform !== 'ios') return { request: false, reason: 'unsupported-platform' }
  if (!nativeAvailable) return { request: false, reason: 'unavailable' }
  return { request: true, reason: 'eligible' }
}

/**
 * The whole decision. Every input is a fact already read; nothing here performs I/O.
 *
 * Order matters only for which REASON is reported — every rule must pass for `request: true`.
 *
 * @param {{
 *   trigger: string,
 *   platform: string,
 *   nativeAvailable: boolean,
 *   enabled: boolean,
 *   session: {count: number, lastActiveAt: number} | null,
 *   lastRequest: {kind: 'never'} | {kind: 'at', at: number} | {kind: 'corrupt'},
 *   now: number,
 * }} facts
 * @returns {{request: boolean, reason: string}}
 */
export function decideReviewRequest({ trigger, platform, nativeAvailable, enabled, session, lastRequest, now }) {
  const precheck = precheckReviewRequest({ trigger, platform, nativeAvailable })
  if (!precheck.request) return precheck
  if (!enabled) return { request: false, reason: 'kill-switch-off' }

  // No readable session record means we cannot show this is not the first session. The bridge
  // writes one at mount, so this is a storage failure or a milestone racing the very first launch —
  // both are "do not ask", and neither is latched, so the next milestone decides again.
  if (!session) return { request: false, reason: 'no-session-record' }
  if (session.count < 2) return { request: false, reason: 'first-session' }

  if (lastRequest.kind === 'corrupt') return { request: false, reason: 'latch-corrupt' }
  if (lastRequest.kind === 'at') {
    const elapsed = now - lastRequest.at
    // A latch more than one whole cooldown in the future cannot be a request this device made on a
    // sane clock; honouring it would lock the device out until that date plus 120 days. It is
    // re-stamped like a corrupt one — the next ask is 120 days from now, never sooner.
    if (elapsed < -REVIEW_REQUEST_COOLDOWN_MS) return { request: false, reason: 'latch-corrupt' }
    // A latch slightly in the future (the clock moved backwards) gives a negative `elapsed`, which
    // is below the cooldown and so reads as "too soon", never "long ago": a bad clock cannot buy a
    // second prompt. The `latchInFuture` fixture pins that.
    if (elapsed < REVIEW_REQUEST_COOLDOWN_MS) return { request: false, reason: 'cooldown' }
  }
  return { request: true, reason: 'eligible' }
}

// One lifecycle write and one milestone at a time per JS process. Both are read-modify-write on
// AsyncStorage, and two overlapping milestones (two sales rung up back to back) would otherwise
// both read "never requested" before either latched.
let sessionWriteChain = Promise.resolve()
let requestInFlight = false

/**
 * The app became active or was touched: a mount of the bridge (a cold start or a JS reload), a
 * return to the foreground, or a touch on the screen. Counts a new session when the last recorded
 * activity is at least `SESSION_INACTIVITY_GAP_MS` old, and records this moment as activity either
 * way — which is why a touch goes through here and not through `touchAppSession`: the first touch on
 * a till left untouched overnight is the start of a new session.
 *
 * A last activity in the FUTURE (the clock went back) is treated as recent — it can delay a count,
 * never invent one — and is overwritten with now, so a clock that was a year ahead does not hold
 * the device in its first session until that date.
 *
 * An unreadable record is REPLACED with a fresh first session rather than incremented: we cannot
 * prove how many sessions preceded it, and "first session" is the conservative reading.
 *
 * @param {{storage?: typeof AsyncStorage, now?: () => number}} [deps]
 * @returns {Promise<{count: number} | null>}
 */
export function recordAppSession({ storage = AsyncStorage, now = () => Date.now() } = {}) {
  return serialiseSessionWrite(async () => {
    const at = now()
    const current = parseSessionRecord(await storage.getItem(SESSION_STORAGE_KEY))
    const isNewSession = !current || at - current.lastActiveAt >= SESSION_INACTIVITY_GAP_MS
    const next = { count: !current ? 1 : isNewSession ? current.count + 1 : current.count, lastActiveAt: at }
    await storage.setItem(SESSION_STORAGE_KEY, JSON.stringify(next))
    return { count: next.count }
  }, 'session could not be recorded')
}

/**
 * The app is leaving the foreground: record the moment as activity. Never counts a session, and
 * never CREATES a record — only `recordAppSession` may say a session began.
 *
 * @param {{storage?: typeof AsyncStorage, now?: () => number}} [deps]
 * @returns {Promise<{count: number} | null>}
 */
export function touchAppSession({ storage = AsyncStorage, now = () => Date.now() } = {}) {
  return serialiseSessionWrite(async () => {
    const current = parseSessionRecord(await storage.getItem(SESSION_STORAGE_KEY))
    if (!current) return null
    await storage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ count: current.count, lastActiveAt: now() }))
    return { count: current.count }
  }, 'session activity could not be recorded')
}

/** Run one session-record read-modify-write after the previous one settles. Never rejects. */
function serialiseSessionWrite(work, failureMessage) {
  const run = async () => {
    try {
      return await work()
    } catch (error) {
      console.warn(`[appReview] ${failureMessage}; app continues`, error)
      return null
    }
  }
  const result = sessionWriteChain.then(run, run)
  sessionWriteChain = result
  return result
}

// The refresh a previous eligible moment started and gave up waiting for, while it is still in
// flight. See `joinRemoteConfigRefresh`.
let refreshInFlight = null

/**
 * Start a Remote Config refresh, or JOIN the one a previous eligible moment started and stopped
 * waiting for. A second call issued while the first is still airborne cannot be fresher than it,
 * and it may settle SOONER — a fetch the SDK coalesces or throttles can resolve at once with the
 * values already activated (how the Firebase iOS SDK treats an overlapping fetch has not been
 * verified here, so this must not depend on it). Settling early would let the sale after a skipped
 * one read the stale switch and latch, defeating the re-read below exactly where it matters.
 * Joining removes the question. Keyed by the refresh function, so a different source is never
 * mistaken for the same fetch.
 *
 * A synchronous throw from `refresh` propagates, as it did before this was shared.
 *
 * @param {() => Promise<unknown>} refresh
 * @returns {Promise<void>} settles (never rejects) when the refresh does
 */
function joinRemoteConfigRefresh(refresh) {
  if (refreshInFlight && refreshInFlight.refresh === refresh) return refreshInFlight.settled
  const settled = Promise.resolve(refresh()).then(
    () => undefined,
    () => undefined,
  )
  const entry = { refresh, settled }
  refreshInFlight = entry
  // Registered before any waiter's handler, so the slot is free by the time a waiter resumes.
  void settled.then(() => {
    if (refreshInFlight === entry) refreshInFlight = null
  })
  return settled
}

/**
 * Resolve `ready` if it settles within `ms`; otherwise report that it did not.
 *
 * @param {Promise<unknown> | undefined} ready
 * @param {number} ms
 * @returns {Promise<boolean>}
 */
async function settlesWithin(ready, ms) {
  if (!ready) return true
  let timer
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => resolve(false), ms)
  })
  try {
    return await Promise.race([Promise.resolve(ready).then(() => true, () => true), timeout])
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Ask for a review if, and only if, every rule above passes.
 *
 * @param {{
 *   trigger: string,
 *   platform: string,
 *   nativeReview: {isAvailable: () => boolean, requestReviewAsync: () => Promise<boolean>},
 *   getRemoteConfigValue: (key: string) => unknown,
 *   remoteConfigReady?: Promise<unknown>,
 *   refreshRemoteConfig?: () => Promise<unknown>,
 *   postHog?: {capture?: (event: string, properties?: Record<string, unknown>) => void} | null,
 *   storage?: typeof AsyncStorage,
 *   now?: () => number,
 *   remoteConfigWaitMs?: number,
 *   onError?: (error: unknown) => void,
 * }} params
 * @returns {Promise<{requested: boolean, reason: string}>}
 */
export async function maybeRequestAppReview({
  trigger,
  platform,
  nativeReview,
  getRemoteConfigValue,
  remoteConfigReady,
  refreshRemoteConfig,
  postHog,
  storage = AsyncStorage,
  now = () => Date.now(),
  remoteConfigWaitMs = REMOTE_CONFIG_WAIT_MS,
  onError,
}) {
  if (requestInFlight) return { requested: false, reason: 'in-flight' }
  requestInFlight = true
  try {
    // Platform and binary first: on Android, web and every pre-module iOS binary this returns
    // before touching storage or Remote Config at all.
    const nativeAvailable = Boolean(nativeReview?.isAvailable?.())
    const precheck = precheckReviewRequest({ trigger, platform, nativeAvailable })
    if (!precheck.request) return { requested: false, reason: precheck.reason }

    // The kill switch is only honoured once Remote Config has had its chance to activate. If the
    // boot fetch is still pending this moment is skipped, NOT treated as ON: default-on is for a
    // key nobody set, never for a value we have not read yet. Not latched — the next milestone
    // decides again.
    if (!(await settlesWithin(remoteConfigReady, remoteConfigWaitMs))) {
      return { requested: false, reason: 'remote-config-pending' }
    }
    const enabled = isReviewPromptEnabled(getRemoteConfigValue(APP_REVIEW_PROMPT_RC_KEY))

    const session = parseSessionRecord(await storage.getItem(SESSION_STORAGE_KEY))
    const rawLastRequest = await storage.getItem(LAST_REQUEST_STORAGE_KEY)
    const lastRequest = parseLastRequest(rawLastRequest)
    const at = now()

    const decision = decideReviewRequest({ trigger, platform, nativeAvailable, enabled, session, lastRequest, now: at })
    if (!decision.request) {
      if (decision.reason === 'latch-corrupt') {
        // Something is stored and it is not a believable instant, so a request MAY have been made
        // recently.
        // Re-stamp it as now: the worst case is one prompt 120 days late, never two inside 120.
        await storage.setItem(LAST_REQUEST_STORAGE_KEY, String(at))
      }
      return { requested: false, reason: decision.reason }
    }

    // The kill switch as it stands NOW, not as it stood at launch. `App.js` keeps the screen awake,
    // so a till never backgrounds and the foreground refetch in `AppStack` never runs: without this
    // a published `false` would not reach a till until it was relaunched, which on a till can be
    // days. Only an ELIGIBLE moment pays for it (once per 120 days per device, plus retries while a
    // refresh does not settle), so it adds no fetch to an ordinary sale. A refresh that does not
    // settle in time skips this moment unlatched, exactly like a pending boot fetch; one that
    // failed has settled, and the activated value decides.
    if (refreshRemoteConfig) {
      if (!(await settlesWithin(joinRemoteConfigRefresh(refreshRemoteConfig), remoteConfigWaitMs))) {
        return { requested: false, reason: 'remote-config-pending' }
      }
      if (!isReviewPromptEnabled(getRemoteConfigValue(APP_REVIEW_PROMPT_RC_KEY))) {
        return { requested: false, reason: 'kill-switch-off' }
      }
    }

    // ⚠️ LATCHED BEFORE THE NATIVE CALL. The other order lets a process that dies between the
    // request and the write ask again at the next milestone; this order can at worst skip one.
    await storage.setItem(LAST_REQUEST_STORAGE_KEY, String(at))

    try {
      await nativeReview.requestReviewAsync()
    } catch (error) {
      // The request was NOT made (typically: no foreground-active window scene because the app was
      // backgrounded between the milestone and this line). Restore the previous latch so this
      // device is not locked out for 120 days by a request that never reached Apple.
      await restoreLatch(storage, rawLastRequest)
      onError?.(error)
      return { requested: false, reason: 'native-error' }
    }

    try {
      // No outcome, no identifiers beyond what PostHog already attaches: Apple does not report
      // whether the dialog appeared, so the event states only that it was requested.
      postHog?.capture?.(APP_REVIEW_REQUESTED_EVENT, {
        trigger,
        platform,
        atlas_env: resolveClientEnv(),
      })
    } catch (error) {
      onError?.(error)
    }
    return { requested: true, reason: 'requested' }
  } catch (error) {
    // Storage threw. Nothing was requested; nothing more can be safely decided.
    onError?.(error)
    return { requested: false, reason: 'error' }
  } finally {
    requestInFlight = false
  }
}

async function restoreLatch(storage, rawLastRequest) {
  try {
    if (rawLastRequest == null) await storage.removeItem(LAST_REQUEST_STORAGE_KEY)
    else await storage.setItem(LAST_REQUEST_STORAGE_KEY, rawLastRequest)
  } catch (error) {
    // Leaving the fresh latch in place is the safe failure: one skipped cycle, never an extra ask.
    console.warn('[appReview] latch could not be restored after a failed request', error)
  }
}
